import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, getShared } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Currency, Loader, RarityBadge, SkullCoin } from "@/components/Atoms";
import { ChilletSkull } from "@/components/ChilletSkull";
import { AvatarUploader } from "@/components/dashboard/AvatarUploader";
import {
  LuckyBreedersHall,
  LegendaryPalsShowcase,
  LuckyPalLeaderboard,
} from "@/components/dashboard/PalHighlights";
import { Coins, Flame, Crown, Server, Link2, Trophy, Wifi, WifiOff, Users, Send, Swords } from "lucide-react";

export default function Dashboard() {
  const { user, refreshProfile } = useAuth();
  const toast = useToast();
  const [cooldowns, setCooldowns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/profile/cooldowns")
      .then((r) => setCooldowns(r.data.cooldowns || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    refreshProfile();
    // eslint-disable-next-line
  }, []);

  if (!user) return <Loader />;

  const avatar = user.discord_avatar_url;
  const initials = (user.username || "?").slice(0, 2).toUpperCase();

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user.username}`}
        subtitle="Your haunted command room awaits."
        right={user.is_admin && <Link to="/admin" className="btn btn-warning btn-sm" data-testid="dashboard-admin-link">Admin Panel</Link>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 fade-up">
        <Card icon={Coins} label="Chillet Coins">
          <div className="text-3xl mt-1"><Currency value={user.points} className="text-3xl" /></div>
          <Link to="/economy" className="text-xs text-[var(--teal)] mt-2 inline-block">Earn more →</Link>
        </Card>

        <Card icon={Flame} label="Daily Streak">
          <div className="text-3xl glow-purple mt-1">{user.daily_streak?.streak ?? 0}</div>
          <div className="text-xs text-[var(--muted)] mt-2">days in a row</div>
        </Card>

        <Card icon={Crown} label="VIP Status">
          {user.vip ? (
            <>
              <div className="text-xl glow-gold mt-1">{user.vip.vip_type || "Active"}</div>
              {user.vip.expires_at && <div className="text-xs text-[var(--muted)] mt-1">Until {new Date(user.vip.expires_at).toLocaleDateString()}</div>}
            </>
          ) : (
            <>
              <div className="text-xl mt-1 text-[var(--muted)]">None</div>
              <Link to="/shop" className="text-xs text-[var(--teal)] mt-2 inline-block">Buy VIP Day Pass →</Link>
            </>
          )}
        </Card>

        <Card icon={Server} label="Linked Servers">
          <div className="text-3xl mt-1 glow-teal">{user.linked_servers?.length || 0}</div>
          <Link to="/linking" className="text-xs text-[var(--teal)] mt-2 inline-block">Manage →</Link>
        </Card>
      </div>

      {/* Profile + Linked Servers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <div className="glass-card p-6 lg:col-span-1 fade-up">
          <h3 className="label mb-4">Profile</h3>
          <AvatarUploader avatar={avatar} initials={initials} username={user.username} />
          <div className="text-xs text-[var(--muted)] mb-3">
            {user.email || user.discord_username || "Discord-linked"}
            {user.is_admin && (
              <span className="badge text-[var(--warning)] border-[var(--warning)] ml-2">Admin</span>
            )}
          </div>
          <div className="text-xs text-[var(--muted)]">
            <span className="block">User ID</span>
            <code className="text-[var(--bone)] text-[11px] break-all">{user.user_id}</code>
          </div>
        </div>

        <div className="glass-card p-6 lg:col-span-2 fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="label">Linked Game Servers</h3>
            <Link to="/linking" className="btn btn-sm" data-testid="dashboard-link-button">
              <Link2 size={12} /> Link in-game
            </Link>
          </div>
          {user.linked_servers?.length ? (
            <ul className="divide-y divide-white/5">
              {user.linked_servers.map((s) => (
                <li key={s.server_name || `${s.player_name}@${s.server_host || ""}`} className="flex items-center justify-between py-2.5 text-sm" data-testid="linked-server-row">
                  <div>
                    <div className="text-[var(--bone)]">{s.server_name}</div>
                    <div className="text-xs text-[var(--muted)]">Player: {s.player_name}</div>
                  </div>
                  <span className="badge text-[var(--success)] border-[var(--success)]">Linked</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-6">
              <ChilletSkull size={36} className="wisp text-[var(--teal)] mx-auto mb-3" />
              <p className="text-sm text-[var(--muted)] mb-3">No characters bound yet.</p>
              <Link to="/linking" className="btn btn-sm" data-testid="dashboard-link-cta">Link your first server</Link>
            </div>
          )}
        </div>
      </div>

      {/* Live server status */}
      <ServerStatusPanel />

      {/* In-game combat stats (PVP + deaths to pals) — bot stats pipeline */}
      <CombatStatsPanel hasLinks={!!user.linked_servers?.length} />

      {/* Cooldowns */}
      {!loading && cooldowns.length > 0 && (
        <div className="glass-card p-6 mt-6 fade-up" style={{ animationDelay: "0.2s" }}>
          <h3 className="label mb-3">Active Cooldowns</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cooldowns.map((c) => (
              <CooldownChip key={c.command} cmd={c.command} expires={c.expires_at} />
            ))}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 fade-up" style={{ animationDelay: "0.3s" }}>
        <QuickLink to="/economy"   icon={Coins}   label="Work" />
        <QuickLink to="/palgame"   icon={ChilletSkull}   label="Catch a Pal" />
        <QuickLink to="/builder"   icon={Send}    label="Send to Game" />
        <QuickLink to="/map"       icon={Server}  label="Explore Map" />
      </div>

      {/* Featured raid pals — eye candy + drives Towers/Map clicks */}
      <FeaturedRaidPals />

      {/* Region Highlights — rarest pals near your in-game position */}
      <RegionHighlights />

      {/* Per-user showcase: Legendary Pals you've caught */}
      <LegendaryPalsShowcase />

      {/* Community social proof: rarest breeds + Lucky-pal leaderboard */}
      <LuckyBreedersHall />
      <LuckyPalLeaderboard />

      {/* Trainer Trophy Shelf — lights up as you defeat each NPC */}
      <TrainerTrophyShelf />
    </div>
  );
}

function TrainerTrophyShelf() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/palgame/trainer-trophies")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  const cleared = data.cleared || 0;
  const total = data.total || 0;
  const pct = total > 0 ? Math.round((cleared / total) * 100) : 0;
  const ASSETS = `${process.env.REACT_APP_BACKEND_URL}/api`;

  return (
    <section className="mt-8 fade-up" style={{ animationDelay: "0.4s" }} data-testid="trainer-trophy-shelf">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-xl flex items-center gap-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            Trainer Trophies
            {cleared >= total && total > 0 && (
              <span
                className="text-[10px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full border border-[var(--gold)]/60 text-[var(--gold)] bg-[rgba(255,215,128,0.08)]"
                title="All 9 trainers defeated"
                data-testid="master-trainer-badge"
              >
                ★ Master Trainer
              </span>
            )}
          </h3>
          <p className="text-xs text-[var(--muted)]">
            Defeat NPC trainers in <Link to="/palgame" className="text-[var(--teal)] underline-offset-2 hover:underline">Pal Game → Battle</Link> to light each badge.
          </p>
        </div>
        <div className="text-sm" data-testid="trainer-trophy-progress">
          <span className="glow-teal font-semibold">{cleared}</span>
          <span className="text-[var(--muted)]">{" / "}{total}</span>
          <span className="text-[var(--muted)] text-xs ml-2">({pct}%)</span>
        </div>
      </div>
      <div className="h-1 rounded-full bg-white/5 overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-[var(--teal)] to-[var(--gold)] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3">
        {(data.trainers || []).map((t) => (
          <TrophyBadge key={t.id} t={t} assets={ASSETS} />
        ))}
      </div>
    </section>
  );
}

function TrophyBadge({ t, assets }) {
  const portrait = t.pal_portrait ? `${assets}/${t.pal_portrait}` : null;
  const lit = t.beaten;
  const locked = t.locked;
  return (
    <div
      className={`relative glass-card p-2 text-center transition-all ${
        lit ? "border border-[var(--gold)]/50 shadow-[0_0_20px_rgba(255,215,128,0.18)]"
            : locked ? "opacity-40" : "opacity-70 hover:opacity-100"
      }`}
      title={`${t.name} · ${t.tier} · Lv ${t.min_level}+`}
      data-testid={`trophy-${t.id}`}
      data-beaten={lit ? "1" : "0"}
    >
      <div className={`w-16 h-16 mx-auto mb-1 rounded-md overflow-hidden flex items-center justify-center bg-black/40 ${
        lit ? "" : "grayscale"
      }`}>
        {portrait ? (
          <img
            src={portrait}
            alt={t.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
          />
        ) : (
          <Trophy size={24} className="text-[var(--muted)]" />
        )}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] truncate">{t.tier}</div>
      <div className="text-xs truncate leading-tight">{t.name.replace(/^.+? /, "")}</div>
      {lit && t.defeats > 1 && (
        <span className="absolute top-1 right-1 bg-[var(--gold)] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
          ×{t.defeats}
        </span>
      )}
      {locked && (
        <span className="absolute top-1 right-1 bg-black/60 text-[var(--muted)] text-[9px] font-medium px-1.5 py-0.5 rounded-full">
          Lv {t.min_level}+
        </span>
      )}
    </div>
  );
}

function RegionHighlights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/map/region-highlights?limit=5")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || !data.markers || data.markers.length === 0) return null;

  const isNearby = data.mode === "nearby";
  const title = isNearby ? "Near You On The Map" : "Region Highlights";
  const subtitle = isNearby
    ? "Rarest pinned encounters closest to your in-game position."
    : "Link your account to see what's near you. Until then, the most fearsome alphas on the island:";
  const CDN = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="mt-8 fade-up" style={{ animationDelay: "0.45s" }} data-testid="region-highlights">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-xl glow-teal" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{title}</h3>
        <Link to="/map" className="text-xs text-[var(--teal)] hover:underline" data-testid="region-highlights-map-link">Open Map →</Link>
      </div>
      <p className="text-xs text-[var(--muted)] mb-3">{subtitle}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {data.markers.map((m) => (
          <Link
            key={m.id}
            to={`/map#marker=${encodeURIComponent(m.id)}`}
            className="glass-card p-3 flex items-center gap-3 hover:border-[var(--teal)] transition-colors"
            data-testid={`region-highlight-${m.id}`}
          >
            <div className="w-12 h-12 rounded-full overflow-hidden border border-[var(--teal-soft)] bg-black/40 shrink-0 flex items-center justify-center">
              {m.icon_url ? (
                <img src={`${CDN}${m.icon_url}`} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <Server size={20} className="text-[var(--teal)]" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm truncate">{m.name}</div>
              <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider">
                {isNearby && m.distance != null
                  ? `${Math.round(m.distance)} tiles away`
                  : (m.description || m.category.replace("_", " "))}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const RAID_PALS = [
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Xenolord.png`,                       name: "Xenolord",         subtitle: "Cosmic Apex" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Jetragon.png`,                       name: "Jetragon",         subtitle: "Speed Apex" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Frostallion.png`,                    name: "Frostallion",      subtitle: "Ice Sovereign" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Frostallion_Noct.png`,               name: "Frostallion Noct", subtitle: "Eclipsed Sovereign" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Paladius.png`,                       name: "Paladius",         subtitle: "Holy Knight" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Necromus.png`,                       name: "Necromus",         subtitle: "Void Rider" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Neptilius.png`,                      name: "Neptilius",        subtitle: "Tide Lord" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Hartalis.jpeg`,                      name: "Hartalis",         subtitle: "King of Salvation" },
  { url: `${process.env.REACT_APP_BACKEND_URL}/api/assets/pal_cards/Xenogard.png`,                      name: "Xenogard",         subtitle: "Dragon Apex" },
];

function FeaturedRaidPals() {
  return (
    <div className="mt-8 fade-up" style={{ animationDelay: "0.4s" }}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xl glow-teal" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Apex Palworld Pals</h3>
        <Link to="/towers" className="text-xs text-[var(--teal)] hover:underline" data-testid="dashboard-towers-link">View Towers →</Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 sm:-mx-6 px-4 sm:px-6" data-testid="raid-pals-carousel">
        {RAID_PALS.map((p) => (
          <div key={p.name} className="shrink-0 w-44 glass-card p-2 hover:border-[var(--teal)] transition-colors">
            <img
              src={p.url}
              alt={p.name}
              className="w-full h-44 object-cover rounded bg-black/40"
              loading="lazy"
              draggable={false}
            />
            <div className="text-sm mt-2 truncate">{p.name}</div>
            <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider">{p.subtitle}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CombatStatsPanel({ hasLinks }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!hasLinks) return;
    api.get("/profile/game-stats")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [hasLinks]);

  if (!hasLinks || !data || !data.totals) return null;
  const t = data.totals;
  const hasAny = t.kills || t.deaths || t.deaths_to_pals;

  return (
    <div className="glass-card p-6 mt-6 fade-up" style={{ animationDelay: "0.18s" }} data-testid="combat-stats-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Swords size={14} className="text-[var(--teal)]" />
          <h3 className="label !mb-0">In-Game Combat Stats</h3>
        </div>
        <span className="text-[10px] text-[var(--muted)] uppercase tracking-wider">
          Tracked live from server death announcements
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatTile label="PVP Kills" value={t.kills} testId="combat-kills" glow="glow-teal" />
        <StatTile label="PVP Deaths" value={t.deaths} testId="combat-deaths" />
        <StatTile label="K / D" value={t.kd} testId="combat-kd" glow="glow-gold" />
        <StatTile label="Deaths to Pals" value={t.deaths_to_pals} testId="combat-pal-deaths" />
      </div>
      {data.nemeses?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="combat-nemeses">
          <span className="text-[var(--muted)] uppercase tracking-wider text-[10px]">Nemeses:</span>
          {data.nemeses.map((n) => (
            <span key={n.entity} className="badge text-[var(--error)] border-[var(--error)]/50">
              {n.entity} ×{n.count}
            </span>
          ))}
        </div>
      )}
      {!hasAny && (
        <p className="text-xs text-[var(--muted)] italic">
          No combat recorded yet — kills, deaths and pal encounters start counting from your next session.
        </p>
      )}
    </div>
  );
}

function StatTile({ label, value, testId, glow = "" }) {
  return (
    <div className="border border-white/8 rounded-md p-3 text-center" data-testid={testId}>
      <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider">{label}</div>
      <div className={`text-2xl mt-1 ${glow}`}>{value ?? 0}</div>
    </div>
  );
}

function Card({ icon: Icon, label, children }) {
  return (
    <div className="glass-card p-5">
      <div className="flex items-center gap-2 text-[var(--muted)]">
        <Icon size={14} />
        <span className="label !mb-0">{label}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="glass-card p-4 flex flex-col items-center justify-center gap-2 hover:border-[var(--teal)]" data-testid={`quick-${label.toLowerCase().replace(/ /g, "-")}-link`}>
      <Icon size={22} className="text-[var(--teal)]" />
      <span className="text-xs uppercase tracking-wider">{label}</span>
    </Link>
  );
}

function CooldownChip({ cmd, expires }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = Math.max(0, new Date(expires).getTime() - now);
  if (remaining === 0) return null;
  const m = Math.floor(remaining / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return (
    <div className="border border-white/8 rounded-md p-3 text-center">
      <div className="text-xs text-[var(--muted)] uppercase tracking-wider">{cmd}</div>
      <div className="text-lg glow-teal mt-1">{m}:{String(s).padStart(2, "0")}</div>
    </div>
  );
}

function ServerStatusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    getShared("/servers/status")
      .then((data) => { setData(data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  if (loading || error || !data) return null;
  const servers = data.servers || [];
  const totalOnline = servers.reduce((acc, s) => acc + (s.player_count || 0), 0);

  return (
    <div className="glass-card p-6 mt-6 fade-up" style={{ animationDelay: "0.15s" }} data-testid="server-status-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wifi size={14} className="text-[var(--success)]" />
          <h3 className="label !mb-0">Now Online</h3>
        </div>
        <span className="text-xs text-[var(--muted)]">
          <Users size={11} className="inline mr-1" />
          {totalOnline} player{totalOnline === 1 ? "" : "s"} across {servers.length} server{servers.length === 1 ? "" : "s"}
          {data.cached ? " · cached" : ""}
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {servers.map((s) => (
          <ServerCard key={s.name} server={s} />
        ))}
      </div>
    </div>
  );
}

function ServerCard({ server }) {
  const uptime = (sec) => {
    if (!sec) return null;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  // Match the BACKEND's slugify rule (`api/routes/live.py::_slugify`)
  // — collapse every run of non-alphanumeric characters into one
  // hyphen, with NO apostrophe pre-strip. The map's existing
  // frontend `slugify` (MapPage.jsx line 17) does strip apostrophes
  // first, but `liveServers[].slug` comes straight from the backend,
  // so "Chillet's Revival Pvpve" → "chillet-s-revival-pvpve"
  // (note the standalone `s`). We mirror that exactly so the
  // `?server=<slug>` deep-link lands on the matching dropdown
  // entry without a slug-mismatch fallback.
  const slug = (server.name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return (
    <Link
      to={`/map?server=${encodeURIComponent(slug)}`}
      className={`block border rounded-md p-4 transition-colors hover:border-[var(--teal)] hover:bg-[rgba(0,229,255,0.04)] focus:outline-none focus:ring-2 focus:ring-[var(--teal-soft)] ${server.online ? "border-[var(--teal-soft)]" : "border-white/5 opacity-60"}`}
      data-testid={`server-card-${server.name.replace(/\W/g, "_")}`}
      title={`Open ${server.name} on the Live Map`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {server.online ? <Wifi size={14} className="text-[var(--success)]" /> : <WifiOff size={14} className="text-[var(--error)]" />}
          <span className="text-sm">{server.name}</span>
        </div>
        <span className={`badge ${server.online ? "text-[var(--success)] border-[var(--success)]" : "text-[var(--error)] border-[var(--error)]"}`}>
          {server.online ? "Online" : "Offline"}
        </span>
      </div>
      <div className="text-xs text-[var(--muted)] mb-3 flex items-center justify-between">
        <code>{server.host}{server.game_port ? `:${server.game_port}` : ""}</code>
        <span>
          {server.player_count}/{server.max_players ?? "?"} · 
          {server.fps ? ` ${Math.round(server.fps)} fps · ` : " "}
          {uptime(server.uptime_seconds) || "—"}
        </span>
      </div>
      {server.players?.length > 0 ? (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto">
          {server.players.map((p) => (
            <li key={p.player_uid || p.name} className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-[var(--success)] flex-shrink-0" style={{ boxShadow: "0 0 6px var(--success)" }} />
              <span className="truncate flex-1" data-testid="online-player-name">{p.name}</span>
              {p.steam_id && <span className="text-[10px] text-[var(--muted)]">{p.steam_id}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-[var(--muted)] italic">{server.online ? "No players online" : "Server unreachable"}</p>
      )}
    </Link>
  );
}
